import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as BackIcon,
  People as UsersIcon,
  Security as SecurityIcon,
  Settings as SystemIcon,
  Dashboard as OverviewIcon,
  AdminPanelSettings as AdminIcon,
  VpnKey as RolesIcon,
  Shield as RBACIcon,
  Extension as ModulesIcon,
  Widgets as MiniModulesIcon,
  History as AuditIcon,
  Notifications as NotificationsAdminIcon,
  VisibilityOutlined as VisibilityIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from '../../components/ThemeToggle';
import NotificationsBell from '../../components/NotificationsBell';
import HeaderChatButton from '../../components/HeaderChatButton';

const drawerWidth = 280;
const collapsedDrawerWidth = 64;

const AdministrationLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { hasRole, user } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleBackToMain = () => {
    navigate('/dashboard');
  };

  const adminMenuItems = [
    {
      text: 'Tableau de bord',
      icon: <OverviewIcon />,
      path: '/administration',
      exact: true,
      miniModuleName: 'Users', // General access
    },
    {
      text: 'Gestion des Utilisateurs',
      icon: <UsersIcon />,
      path: '/administration/users',
      miniModuleName: 'Users',
    },
    {
      text: 'Gestion des Rôles',
      icon: <RolesIcon />,
      path: '/administration/roles',
      miniModuleName: 'Roles',
    },
    {
      text: 'Gestion des Permissions',
      icon: <RBACIcon />,
      path: '/administration/permissions',
      miniModuleName: 'Profiles',
    },
    {
      text: 'Gestion des Modules',
      icon: <ModulesIcon />,
      path: '/administration/modules',
      miniModuleName: 'Modules',
      developerOnly: true, // Only visible to developer role
    },
    {
      text: 'Gestion des Mini-Modules',
      icon: <MiniModulesIcon />,
      path: '/administration/mini-modules',
      miniModuleName: 'MiniModules',
      developerOnly: true, // Only visible to developer role
    },
    {
      text: 'Sécurité',
      icon: <SecurityIcon />,
      path: '/administration/security',
      miniModuleName: null,
      comingSoon: true
    },
    {
      text: 'Journal d\'Audit',
      icon: <AuditIcon />,
      path: '/administration/audit-logs',
      miniModuleName: 'Audit Logs',
    },
    {
      text: 'Notifications',
      icon: <NotificationsAdminIcon />,
      path: '/administration/notifications',
      miniModuleName: null,
    },
    {
      text: 'Visibilité Commerciale',
      icon: <VisibilityIcon />,
      path: '/administration/commercial-visibility',
      miniModuleName: null,
    },
    {
      text: 'Paramètres Avancés',
      icon: <AdminIcon />,
      path: '/administration/settings',
      miniModuleName: null,
      comingSoon: true
    },
  ];

  // Check permissions for each menu item (but show all items)
  const menuItemsWithPermissions = adminMenuItems.map((item) => {
    if (item.comingSoon) return { ...item, hasAccess: false }; // Coming soon items are disabled

    // Developer-only items: STRICTLY check for developer role only
    if ((item as any).developerOnly) {
      const userRoleName = user?.user_role?.name?.toLowerCase() || '';
      if (userRoleName !== 'developer') {
        return { ...item, hasAccess: false }; // Disable for non-developers
      }
    }

    if (!item.miniModuleName) return { ...item, hasAccess: true }; // No permission check needed
    const hasAccess = hasPermission('Admin', 'view', item.miniModuleName);
    return { ...item, hasAccess };
  });

  const isActiveRoute = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <div>
      <Toolbar>
        {desktopOpen ? (
          <Button
            fullWidth
            startIcon={<BackIcon />}
            onClick={handleBackToMain}
            variant="outlined"
            size="small"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', justifyContent: 'flex-start' }}
          >
            Retour au Menu Principal
          </Button>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <IconButton
              onClick={handleBackToMain}
              size="small"
            >
              <BackIcon />
            </IconButton>
          </Box>
        )}
      </Toolbar>
      <Divider />
      

      
      <List>
        {menuItemsWithPermissions.map((item) => {
          const isDisabled = item.comingSoon || !item.hasAccess;
          const isActive = !isDisabled && isActiveRoute(item.path, item.exact);

          const tooltipTitle = !item.hasAccess && !item.comingSoon ? 'Accès refusé' : '';

          return (
            <ListItem key={item.text} disablePadding>
              <Tooltip title={tooltipTitle} placement="right" arrow>
                <span style={{ width: '100%' }}>
                  <ListItemButton
                    selected={isActive}
                    onClick={!isDisabled ? () => navigate(item.path) : undefined}
                    disabled={isDisabled}
                    sx={{
                      minHeight: 48,
                      justifyContent: desktopOpen ? 'initial' : 'center',
                      px: desktopOpen ? 2.5 : 0,
                      '&.Mui-selected': {
                        backgroundColor: 'error.light',
                        color: 'error.contrastText',
                        '&:hover': {
                          backgroundColor: 'error.main',
                        },
                      },
                      '&.Mui-disabled': {
                        opacity: 0.6,
                        cursor: 'not-allowed',
                        '& .MuiListItemIcon-root': {
                          color: 'text.disabled',
                        },
                        '& .MuiListItemText-root': {
                          color: 'text.disabled',
                        },
                      },
                      cursor: !isDisabled ? 'pointer' : 'not-allowed',
                    }}
                  >
                <ListItemIcon sx={{
                  minWidth: 0,
                  mr: desktopOpen ? 3 : 0,
                  ml: desktopOpen ? 0 : 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: isActive
                    ? 'error.contrastText'
                    : !isDisabled
                      ? 'inherit'
                      : 'text.disabled'
                }}>
                  {item.icon}
                </ListItemIcon>
                {desktopOpen && (
                  <ListItemText
                    primary={item.text}
                    secondary={item.comingSoon ? 'Bientôt disponible' : undefined}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 600 : 400,
                      color: !isDisabled ? 'inherit' : 'text.disabled',
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      fontStyle: 'italic',
                    }}
                  />
                )}
                  </ListItemButton>
                </span>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: {
            sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${collapsedDrawerWidth}px)`
          },
          ml: {
            sm: desktopOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px`
          },
          bgcolor: '#F44336',
          transition: 'width 0.3s ease, margin 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDesktopDrawerToggle}
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Module Administration
          </Typography>
          <HeaderChatButton />
          <NotificationsBell />
          <ThemeToggle />
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        onMouseEnter={() => {
          if (!desktopOpen) setDesktopOpen(true);
        }}
        sx={{
          width: { sm: desktopOpen ? drawerWidth : collapsedDrawerWidth },
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease'
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth 
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: desktopOpen ? drawerWidth : collapsedDrawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
          open={desktopOpen}
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        onClick={() => {
          if (desktopOpen) setDesktopOpen(false);
        }}
        sx={{
          flexGrow: 1,
          py: 3,
          px: 2,
          width: {
            sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${collapsedDrawerWidth}px)`
          },
          ml: {
            sm: 0
          },
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdministrationLayout;